import React, { useMemo, useState } from 'react';
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import {
  FiBox,
  FiChevronLeft,
  FiChevronRight,
  FiFile,
  FiFileText,
  FiLink,
  FiPlay,
  FiTruck,
  FiZap,
} from 'react-icons/fi';
import { ff } from '../../../theme';

/**
 * Brand-typed links can omit the scheme (e.g. "link.com"); window.open / href then resolves
 * them against the app origin. Force an absolute external URL. Signed storage URLs and links
 * that already have a scheme pass through unchanged.
 */
function toExternalHref(url: string): string {
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** Brief section card: 20px radius, hairline border (matches CreativeBrief cards). */
const CARD_ITEM = {
  border: '1px solid',
  borderColor: ff.line,
  borderRadius: '20px',
  overflow: 'hidden',
  mx: 4,
  mt: 3,
  bg: 'white',
} as const;

export function MarketplaceAccordionSummaryIcon(props: { icon: React.ElementType }) {
  return (
    <Flex
      w="36px"
      h="36px"
      borderRadius="12px"
      align="center"
      justify="center"
      flexShrink={0}
      bg={ff.grey}
    >
      <Box as="span" color={ff.ink}>
        <Box as={props.icon} boxSize="18px" />
      </Box>
    </Flex>
  );
}

/** Row inside AccordionButton: title column left-aligned; optional trailing (e.g. cash) next to chevron. */
export function MarketplaceAccordionSummaryTrigger(props: {
  icon: React.ElementType;
  overline?: string;
  title: string;
  trailing?: React.ReactNode;
}) {
  return (
    <Flex w="100%" align="center" gap={3} minH="68px" py={0} fontFamily="display">
      <MarketplaceAccordionSummaryIcon icon={props.icon} />
      <Box flex="1" minW={0} textAlign="left">
        {props.overline ? (
          <Text textStyle="eyebrow" color={ff.text2} mb={1}>
            {props.overline}
          </Text>
        ) : null}
        <Text fontSize="16px" fontWeight="600" color={ff.ink} noOfLines={1} textAlign="left">
          {props.title}
        </Text>
      </Box>
      <Flex align="center" gap={2} flexShrink={0}>
        {props.trailing}
        <AccordionIcon color={ff.ink} boxSize={5} />
      </Flex>
    </Flex>
  );
}

export type MarketplaceBriefAccordionModel = {
  deliverables: Array<{ label: string; count: number }>;
  bundleTitle: string;
  cash: number;
  narrative?: string | null;
  contentBriefFile?: string | null;
  fulfillmentType?: string | null;
  idealTimelineDisplay?: string | null;
  inspirationLinks: string[];
  /** Brand-uploaded inspiration images/videos as signed read URLs. */
  inspirationAssetUrls?: string[];
  otherNotes?: string | null;
};

/** Classify an asset by its file extension (ignoring any signed-URL query string). */
function inspirationAssetKind(url: string): 'image' | 'video' | 'other' {
  const path = url.split('?')[0].toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|avif|heic|heif|bmp|svg)$/.test(path)) return 'image';
  if (/\.(mp4|mov|webm|m4v|ogv|avi|mkv)$/.test(path)) return 'video';
  return 'other';
}

type InspirationAsset = { url: string; kind: 'image' | 'video' | 'other' };

/** Centered popup that plays/shows one asset at a time with prev/next navigation and an X to close. */
function InspirationLightbox(props: {
  assets: InspirationAsset[];
  index: number | null;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { assets, index } = props;
  const isOpen = index !== null;
  const current = isOpen ? assets[index] : null;
  return (
    <Modal isOpen={isOpen} onClose={props.onClose} isCentered size="xl">
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        w="fit-content"
        maxW="min(880px, 92vw)"
        m={0}
      >
        <ModalCloseButton
          color="white"
          bg="blackAlpha.600"
          borderRadius="full"
          w="44px"
          h="44px"
          top={2}
          right={2}
          zIndex={2}
          _hover={{ bg: 'blackAlpha.700' }}
        />
        <ModalBody p={0} position="relative" display="flex" alignItems="center" justifyContent="center">
          {current?.kind === 'video' ? (
            // No forced width: the video keeps its own aspect ratio, capped by maxW/maxH, so
            // portrait (vertical) clips stay tall-and-narrow instead of being pillarboxed.
            <Box
              as="video"
              src={current.url}
              controls
              autoPlay
              maxW="min(880px, 92vw)"
              maxH="80vh"
              bg={ff.ink}
              borderRadius="20px"
              display="block"
            />
          ) : current ? (
            <Image
              src={current.url}
              alt="Inspiration"
              maxW="min(880px, 92vw)"
              maxH="80vh"
              objectFit="contain"
              borderRadius="20px"
            />
          ) : null}

          {assets.length > 1 && (
            <>
              <IconButton
                aria-label="Previous"
                size="lg"
                icon={<FiChevronLeft />}
                onClick={props.onPrev}
                position="absolute"
                left={2}
                top="50%"
                transform="translateY(-50%)"
                borderRadius="full"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.700' }}
              />
              <IconButton
                aria-label="Next"
                size="lg"
                icon={<FiChevronRight />}
                onClick={props.onNext}
                position="absolute"
                right={2}
                top="50%"
                transform="translateY(-50%)"
                borderRadius="full"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.700' }}
              />
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function InspirationAssetGrid(props: { urls: string[] }) {
  const assets = useMemo<InspirationAsset[]>(
    () => props.urls.map((url) => ({ url, kind: inspirationAssetKind(url) })),
    [props.urls],
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (assets.length === 0) return null;

  const step = (dir: 1 | -1) =>
    setLightboxIndex((i) => (i === null ? i : (i + dir + assets.length) % assets.length));

  return (
    <>
      <Flex mt={2} gap={2.5} wrap="wrap">
        {assets.map((asset, idx) => {
          if (asset.kind === 'image') {
            return (
              <Image
                key={asset.url}
                src={asset.url}
                alt={`Inspiration ${idx + 1}`}
                onClick={() => setLightboxIndex(idx)}
                cursor="pointer"
                objectFit="cover"
                w="96px"
                h="96px"
                borderRadius="14px"
                border="1px solid"
                borderColor={ff.line}
              />
            );
          }
          if (asset.kind === 'video') {
            return (
              <Box
                key={asset.url}
                position="relative"
                onClick={() => setLightboxIndex(idx)}
                cursor="pointer"
                w="160px"
                h="96px"
                borderRadius="14px"
                overflow="hidden"
                border="1px solid"
                borderColor={ff.line}
                bg={ff.ink}
              >
                <Box as="video" src={asset.url} muted preload="metadata" w="100%" h="100%" objectFit="cover" />
                <Flex position="absolute" inset={0} align="center" justify="center">
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="blackAlpha.600"
                    align="center"
                    justify="center"
                  >
                    <Box as={FiPlay} color="white" boxSize="16px" />
                  </Flex>
                </Flex>
              </Box>
            );
          }
          return (
            <Flex key={asset.url} align="center" gap={2} py={2} w="100%">
              <Box as={FiLink} color={ff.ink} />
              <Text
                fontSize="15px"
                color={ff.ink}
                textDecoration="underline"
                textDecorationColor={ff.border}
                cursor="pointer"
                onClick={() => window.open(toExternalHref(asset.url), '_blank')}
                noOfLines={1}
              >
                Inspiration file {idx + 1}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      <InspirationLightbox
        assets={assets}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() => step(-1)}
        onNext={() => step(1)}
      />
    </>
  );
}

/**
 * A short, human label for the brief file link instead of the (very long, signed) URL.
 * Uploaded files → original filename (UUID prefix stripped); pasted links → their domain.
 */
function contentBriefFileLabel(url: string): string {
  try {
    const parsed = new URL(url);
    const isUpload = parsed.hostname.endsWith('googleapis.com') && parsed.pathname.includes('marketplaceOrders');
    if (isUpload) {
      const last = decodeURIComponent(parsed.pathname.split('/').pop() ?? '');
      const name = last.replace(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i,
        '',
      );
      if (name) return name;
    }
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return 'View file';
  }
}

function ContentBriefFileSection(props: { url: string; hasDividerAbove: boolean }) {
  const { url, hasDividerAbove } = props;
  return (
    <Box
      mt={hasDividerAbove ? 4 : 0}
      pt={hasDividerAbove ? 4 : 0}
      borderTop={hasDividerAbove ? '1px solid' : undefined}
      borderColor={ff.line}
    >
      <Text textStyle="eyebrow" color={ff.text2} mb={2}>
        Content brief file
      </Text>
      <Flex align="center" gap={2} py={1}>
        <Box as={FiFileText} color={ff.ink} />
        <Text
          fontSize="15px"
          color={ff.ink}
          textDecoration="underline"
          textDecorationColor={ff.border}
          cursor="pointer"
          onClick={() => window.open(toExternalHref(url), '_blank')}
          noOfLines={1}
          title={contentBriefFileLabel(toExternalHref(url))}
        >
          {contentBriefFileLabel(toExternalHref(url))}
        </Text>
      </Flex>
    </Box>
  );
}

/** Five accordion sections shared by accept-order and in-progress marketplace order screens. */
export function MarketplaceOrderBriefAccordionItems(props: { model: MarketplaceBriefAccordionModel }) {
  const {
    deliverables,
    bundleTitle,
    cash,
    narrative,
    contentBriefFile,
    fulfillmentType,
    idealTimelineDisplay,
    inspirationLinks,
    inspirationAssetUrls,
    otherNotes,
  } = props.model;

  const inspirationAssets = inspirationAssetUrls ?? [];

  const narrativeTrimmed = narrative?.trim() ?? '';
  const briefFileTrimmed = contentBriefFile?.trim() ?? '';
  const notesTrimmed = otherNotes?.trim() ?? '';

  return (
    <>
      <AccordionItem {...CARD_ITEM}>
        <AccordionButton px={4} py={0} _hover={{ bg: ff.grey }}>
          <MarketplaceAccordionSummaryTrigger
            icon={FiBox}
            overline="Package"
            title={bundleTitle}
            trailing={
              <Text textStyle="display" fontSize="18px" color={ff.ink}>
                ${cash}
              </Text>
            }
          />
        </AccordionButton>
        <AccordionPanel px={4} pt={0} pb={4}>
          <Box>
            {deliverables.map((d) => (
              <Flex
                key={d.label}
                minH="44px"
                py={2}
                borderBottom="1px solid"
                borderColor={ff.line}
                align="center"
                justify="space-between"
              >
                <Text fontSize="15px" color={ff.text2}>
                  {d.label}
                </Text>
                <Text fontSize="15px" fontWeight="600" color={ff.ink}>
                  ×{d.count}
                </Text>
              </Flex>
            ))}
          </Box>
          {narrativeTrimmed ? (
            <Text mt={3} fontSize="14px" color={ff.text2} lineHeight="1.5">
              {narrativeTrimmed}
            </Text>
          ) : null}
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem {...CARD_ITEM}>
        <AccordionButton px={4} py={0} _hover={{ bg: ff.grey }}>
          <MarketplaceAccordionSummaryTrigger icon={FiFileText} title="From the brand" />
        </AccordionButton>
        <AccordionPanel px={4} pt={0} pb={4}>
          <Text fontSize="15px" color={ff.ink} lineHeight="24.38px" whiteSpace="pre-wrap">
            {narrativeTrimmed || '—'}
          </Text>
          {briefFileTrimmed ? (
            <ContentBriefFileSection url={briefFileTrimmed} hasDividerAbove={Boolean(narrativeTrimmed)} />
          ) : null}
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem {...CARD_ITEM}>
        <AccordionButton px={4} py={0} _hover={{ bg: ff.grey }}>
          <MarketplaceAccordionSummaryTrigger icon={FiTruck} title="Logistics" />
        </AccordionButton>
        <AccordionPanel px={4} pt={0} pb={4}>
          <Box borderBottom="1px solid" borderColor={ff.line} pb={3} mb={3}>
            <Text fontSize="15px" fontWeight="600" color={ff.ink}>
              {fulfillmentType?.trim() || '—'}
            </Text>
            <Text fontSize="13px" color={ff.text2}>
              Brand will send access (login, link, etc.) via chat.
            </Text>
          </Box>
          <Flex align="center" justify="space-between" minH="44px">
            <Text fontSize="15px" color={ff.text2}>
              Ideal timeline
            </Text>
            <Text fontSize="15px" fontWeight="600" color={ff.ink}>
              {idealTimelineDisplay?.trim() || '—'}
            </Text>
          </Flex>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem {...CARD_ITEM}>
        <AccordionButton px={4} py={0} _hover={{ bg: ff.grey }}>
          <MarketplaceAccordionSummaryTrigger icon={FiZap} title="Inspiration" />
        </AccordionButton>
        <AccordionPanel px={4} pt={0} pb={4}>
          <Box>
            {inspirationLinks.length === 0 && inspirationAssets.length === 0 ? (
              <Text fontSize="15px" color={ff.text2}>
                —
              </Text>
            ) : (
              <>
                {inspirationLinks.map((link) => (
                  <Flex key={link} align="center" gap={2} py={2}>
                    <Box as={FiLink} color={ff.ink} />
                    <Text
                      fontSize="15px"
                      color={ff.ink}
                      textDecoration="underline"
                      textDecorationColor={ff.border}
                      cursor="pointer"
                      onClick={() => window.open(toExternalHref(link), '_blank')}
                      noOfLines={1}
                    >
                      {link}
                    </Text>
                  </Flex>
                ))}
                <InspirationAssetGrid urls={inspirationAssets} />
              </>
            )}
          </Box>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem {...CARD_ITEM}>
        <AccordionButton px={4} py={0} _hover={{ bg: ff.grey }}>
          <MarketplaceAccordionSummaryTrigger icon={FiFile} title="Notes" />
        </AccordionButton>
        <AccordionPanel px={4} pt={0} pb={4}>
          <Text fontSize="15px" color={ff.ink} lineHeight="24.38px" whiteSpace="pre-wrap">
            {notesTrimmed || '—'}
          </Text>
        </AccordionPanel>
      </AccordionItem>
    </>
  );
}
